import React, { useState } from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import { Typography, useTheme, Grid } from "@mui/joy";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import { Button, CircularProgress } from "@mui/material";
import { LinkOffRounded } from "@mui-symbols-material/w300";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { BasicModalContent } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { DeactivateIoTConnection } from "../../graphql/mutations/deactivateIoTConnection";

interface Props {
    open: boolean;
    onClose: () => void;
    assetLocationId: string;
    iotConnectionId: string;
}

const DetachIoTConnectionModal = ({ open, onClose, assetLocationId, iotConnectionId }: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();

    const [submitting, setSubmitting] = useState(false);
    const [deactivate] = useMutation(DeactivateIoTConnection, {
        refetchQueries: [
            "AssetIotConnectionId",
            "AssetAssetLocation",
            "AssetIotConnection",
            "AssetIoTConnectionStatus",
            "AssetIoTHubStatus",
            "AssetDashboardHero",
            "AssetNetworkInfo",
            "AssetNameplateInfo",
            "AssetTelemetryAttributes",
            "AssetWarningErrorTotal",
            "InstalledBaseNode",
            "InstalledBaseNodeList",
            "SearchAssets",
            "SearchSystems",
            "SystemList",
            "SystemIotConnection",
            "SystemIoTConnectionStatus",
            "SystemDashboardHero",
            "SystemNetworkInfo",
            "GridDetail",
        ],
        awaitRefetchQueries: true,
    });

    const handleSubmit = () => {
        setSubmitting(true);
        deactivate({
            variables: { assetLocationId, iotConnectionId },
        })
            .then((res) => {
                const success = res.data?.deactivateIoTConnection?.success;
                const message = res.data?.deactivateIoTConnection?.message;
                if (success) {
                    toast.success(message || t("IoT connection detached successfully."));
                    onClose();
                } else {
                    toast.error(message || t("Failed to detach IoT connection."));
                }
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t));
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            fullScreen={isFullScreen}
            slotProps={{
                paper: {
                    sx: {
                        minWidth: isFullScreen ? undefined : "500px",
                    },
                },
            }}
        >
            <BasicModalContent
                startIcon={<LinkOffRounded />}
                onClose={submitting ? undefined : onClose}
                title={<Typography level="titleSmallLight">{t("Detach IoT Connection")}</Typography>}
            >
                <Box display="flex" flexDirection="column" gap="20px">
                    <Box
                        borderRadius="4px"
                        border="1px solid #ccc"
                        padding="12px 16px"
                        marginTop="8px"
                    >
                        <Typography level="body-md">
                            {t("Are you sure you want to detach the IoT connection? This action will deactivate the link between the asset and the IoT device.")}
                        </Typography>
                    </Box>

                    <Grid container spacing={1} sx={{ pt: "8px" }}>
                        <Grid xs={6}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={onClose}
                                disabled={submitting}
                            >
                                {t("Cancel")}
                            </Button>
                        </Grid>
                        <Grid xs={6}>
                            <Button
                                variant="contained"
                                color="error"
                                fullWidth
                                onClick={handleSubmit}
                                disabled={submitting}
                                startIcon={submitting ? <CircularProgress size={16} /> : <LinkOffRounded />}
                            >
                                {t("Detach")}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default DetachIoTConnectionModal;
